import { useState, useEffect, useCallback, useRef } from 'react';

const WS_RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 2;

export function useWebSocket(roomId, onMessage) {
  const [status, setStatus] = useState('disconnected');
  const [userCount, setUserCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref updated without triggering reconnects
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${wsHost}/api/ws/${roomId}`;
  }, [roomId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      // Mark as intentional close
      wsRef.current.intentionalClose = true;
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    if (mountedRef.current) {
      setStatus('disconnected');
    }
  }, []);

  const connect = useCallback(() => {
    if (!roomId || !mountedRef.current) return;
    
    // Clean up any existing connection first
    if (wsRef.current) {
      wsRef.current.intentionalClose = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    if (!mountedRef.current) return;
    setStatus('connecting');
    
    try {
      const wsUrl = getWebSocketUrl();
      const ws = new WebSocket(wsUrl);
      ws.intentionalClose = false;
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        setStatus('connected');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'connected') {
            setUserCount(data.user_count);
          } else if (data.type === 'user_joined') {
            setUserCount(data.user_count);
          } else if (data.type === 'user_left') {
            setUserCount(data.user_count);
          }
          
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (e) {
          console.error('Failed to parse message:', e);
        }
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        
        wsRef.current = null;
        setStatus('disconnected');
        
        // Don't reconnect on intentional close, room full, or rate limited
        if (ws.intentionalClose || event.code === 1000 || event.code === 4003 || event.code === 4029) {
          if (event.code === 4003) {
            setStatus('error');
          }
          return;
        }
        
        // Attempt reconnect for unexpected closes
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && mountedRef.current) {
          reconnectAttemptsRef.current++;
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              connect();
            }
          }, WS_RECONNECT_DELAY);
        } else if (mountedRef.current) {
          setStatus('error');
        }
      };

      ws.onerror = () => {
        // Error will trigger onclose
      };
    } catch (e) {
      if (mountedRef.current) {
        setStatus('error');
      }
    }
  }, [roomId, getWebSocketUrl]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  // Connect on mount, disconnect on unmount - handle StrictMode double mount
  useEffect(() => {
    mountedRef.current = true;
    
    // Small delay to handle StrictMode double mount
    const connectTimeout = setTimeout(() => {
      if (mountedRef.current && roomId) {
        connect();
      }
    }, 100);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(connectTimeout);
      disconnect();
    };
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status,
    userCount,
    send,
    disconnect,
    reconnect: connect,
  };
}
